import { useEffect, useState } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';
import { apiRequest } from '../../api/client';
import { docEditorActive } from '../../lib/mode';

/**
 * The version timeline beside a document.
 *
 * Every change to a page — AI generation, translation, a human editing session, a restore — is
 * one entry. It exists so a disappointing page can be traced back to the prompt and the
 * context that produced it, rather than guessed at.
 *
 * NO LONGER ITS OWN STRANGLER MOUNT. `#doclogs` is a cell of `#docnav-grid`, so once the doc
 * view became React the timeline had nowhere separate to live — it is a component OF that
 * region now, the same move `PresenceStack` made when the six headers that draw it were ported.
 * It renders its own host element instead of filling one, which is why `host` is gone.
 *
 * IT STILL DECIDES FOR ITSELF whether to exist — edit mode, the document's EDIT SCOPE, and a
 * page actually open — and reports that upwards, because its presence changes the doc view's
 * grid template and the clearance above the parent's floating pills. The decision is the
 * panel's; what the decision MEANS to the layout belongs to the layout.
 * `docLogsVisibilityChanged` is retired with the vanilla measuring code it used to drive.
 *
 * WHO OPENS IT (V0.41): the same edit scope that may rewrite the page — its responsibles, an
 * owning node's responsibles, EDIT_ALL_DOCS — not AI_ADMIN alone. The server has served this
 * population since V0.38 (`mayReadDocLog` on GET /api/docs/revisions), so this is the client
 * catching up: a regeneration warning that points a responsible at their revision log must not
 * point at a panel they cannot open.
 *
 * STALENESS IS PER LANGUAGE. A translation carries the source version it was made from, so
 * "2 versions behind EN" is a fact the server can state rather than a guess. That is the whole
 * reason the source version is recorded at all.
 */

interface Coverage {
  status: string;
}

interface Revision {
  id: string;
  version: number;
  createdAt: string;
  source: string;
  label?: string;
  sourceLocale?: string;
  sourceVersion?: number | null;
  imagesDropped?: number;
  coverage?: Coverage[];
}

interface LogPayload {
  revisions?: Revision[];
  sourceLocale?: string;
  staleness?: { stale?: boolean; behind?: number; sourceVersion?: number | null };
}

/** dd.mm.yy — compact, because the pill is narrow and the year is rarely the question. */
function shortDate(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)}`;
}

/** What went wrong in this revision, if anything, as one short phrase. */
function warnings(rev: Revision): string[] {
  const out: string[] = [];
  if ((rev.imagesDropped ?? 0) > 0) {
    out.push(`${rev.imagesDropped} invented image${rev.imagesDropped === 1 ? '' : 's'} removed`);
  }
  const partial = (rev.coverage || []).filter((c) => c.status !== 'done').length;
  if (partial) out.push(`${partial} instruction${partial === 1 ? '' : 's'} not fully done`);
  return out;
}

export function DocLogTimeline({ onVisibleChange }: { onVisibleChange: (v: boolean) => void }) {
  const s = useAppState();
  const [data, setData] = useState<LogPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Edit mode, a document actually open, and the caller in that document's EDIT SCOPE. All
  // three, because the timeline is an authoring tool: a reader has no use for the prompt that
  // produced the page they are on, but a responsible restoring their own page does. That is
  // exactly "the editor is on screen" (`docEditorActive`, V0.53.5).
  const visible = docEditorActive(s);

  useEffect(() => {
    // Not merely hidden: an invisible-but-present panel would still hold a grid column, and
    // the doc view reserves that column only when there is something to put in it.
    onVisibleChange(visible);
  }, [visible, onVisibleChange]);

  useEffect(() => {
    if (!visible || !s.docCurrentId) {
      setData(null);
      return;
    }
    let live = true;
    setLoading(true);
    setError(null);
    const q = new URLSearchParams({ docPageId: s.docCurrentId, locale: s.currentLocale });
    void apiRequest<LogPayload>(`/api/docs/revisions?${q.toString()}`).then(
      (d) => {
        if (!live) return;
        setData(d);
        setLoading(false);
      },
      (e: Error) => {
        if (!live) return;
        setError(e.message);
        setLoading(false);
      },
    );
    return () => {
      live = false;
    };
    // `docRevision` is bumped by the vanilla side whenever a save or a restore adds an entry,
    // which is the only way this panel can know its own list just changed.
  }, [visible, s.docCurrentId, s.currentLocale, s.docRevision]);

  if (!visible) return null;

  const revisions = data?.revisions || [];
  const stale = data?.staleness;
  const sourceLocale = (data?.sourceLocale || '').toUpperCase();

  return (
    <div className="doclogs" id="doclogs">
      <div className="doclogs-head">
        <span>Logs</span>
        <span className="doclogs-loc" id="doclogs-locale">
          {s.currentLocale.toUpperCase()}
        </span>
      </div>

      {/* "2 versions behind EN" — the point of recording which source version a translation
          was made from. Without it, a stale page looks exactly like a current one. */}
      {stale?.stale ? (
        <div id="doclogs-stale" className="doclogs-stale">
          {stale.sourceVersion === null
            ? `Not translated yet — ${sourceLocale} is at V${stale.behind}.`
            : `${stale.behind} version${stale.behind === 1 ? '' : 's'} behind ${sourceLocale} — retranslate to catch up.`}
        </div>
      ) : null}

      <div id="doclogs-list">
        {error ? (
          <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>
            Could not load the log ({error}).
          </div>
        ) : loading && !revisions.length ? (
          <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>Loading…</div>
        ) : !revisions.length ? (
          <div style={{ fontSize: 12.5, color: 'var(--ink-faint)' }}>
            No history for this language yet.
          </div>
        ) : (
          revisions.map((rev) => {
            const warn = warnings(rev);
            return (
              <button
                key={rev.id}
                type="button"
                className={`logpill src-${rev.source}`}
                onClick={() => callAction('openLogDetail', rev.id)}
              >
                <span className="lp-top">
                  <span className="lp-src" />
                  <span className="lp-v">V{rev.version}</span>
                  <span className="lp-date">{shortDate(rev.createdAt)}</span>
                </span>
                <span className="lp-label">{rev.label || ''}</span>
                {rev.sourceLocale ? (
                  <span className="lp-label" style={{ opacity: 0.75 }}>
                    from {rev.sourceLocale.toUpperCase()} V{rev.sourceVersion ?? '?'}
                  </span>
                ) : null}
                {warn.length ? <span className="lp-warn">⚠ {warn.join(' · ')}</span> : null}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
